'use client';

import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'sonner';
import HeroCard from './HeroCard';
import FormProgress from './FormProgress';
import Section1PersonalInfo from './Section1PersonalInfo';
import Section2RSVP from './Section2RSVP';
import Section3BhopalJourney from './Section3BhopalJourney';
import Section4BhopalStay from './Section4BhopalStay';
import Section5FreeDays from './Section5FreeDays';
import Section6BhopalWarud from './Section6BhopalWarud';
import Section7WarudStay from './Section7WarudStay';
import Section8AfterWedding from './Section8AfterWedding';
import Section9TravelSummary from './Section9TravelSummary';
import Section10Misc from './Section10Misc';
import SuccessScreen from './SuccessScreen';

export type RSVPFormData = {
  fullName: string;
  phone: string;
  email?: string;
  origin: string;
  travellingWith: string;
  guestCount: number;
  attendance: 'engagement' | 'wedding' | 'both' | 'neither' | '';
  certainty: string;
  bhopalTransport: string;
  bhopalArrivalDate: string;
  bhopalArrivalTime: string;
  bhopalArrivalPoint: string;
  bhopalPickup: string;
  bhopalAccommodation: string;
  bhopalCheckIn: string;
  bhopalCheckOut: string;
  bhopalRooms: number;
  bhopalRoomGuests: string;
  freeDaysPlan: string;
  exploreInterest: string;
  experience: string[];
  destination: string[];
  groupTrip: string;
  warudTransport: string;
  bhopalWarudCab: string;
  warudTravellers: number;
  warudTravelDate: string;
  warudAccommodation: string;
  warudCheckIn: string;
  warudCheckOut: string;
  warudRooms: number;
  warudRoomGuests: string;
  afterWedding: string;
  warudNagpurCab: string;
  nagpurDrop: string;
  nagpurTravelDate: string;
  flightTime: string;
  nagpurDepartureTime: string;
  travelSummary: string;
  food: string;
  dietaryRequirements: string;
  additionalNotes: string;
};

export default function RSVPPageClient() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState('');
  const [submittedAttendance, setSubmittedAttendance] = useState('');

  const methods = useForm<RSVPFormData>({
    defaultValues: {
      guestCount: 1,
      bhopalRooms: 1,
      warudRooms: 1,
      experience: [],
      destination: [],
    },
    mode: 'onBlur',
  });

  const attendance = methods.watch('attendance');

  const showBhopal =
    attendance === 'engagement' || attendance === 'both';
  const showWarud =
    attendance === 'wedding' || attendance === 'both';
  const showSummary =
    attendance && attendance !== 'neither';

  const onSubmit = async (data: RSVPFormData) => {
    setIsSubmitting(true);

    try {
      const res = await fetch('/api/rsvp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await res.json();

      if (!res.ok) {
        toast.error(result.error || 'Something went wrong. Please try again.');
        setIsSubmitting(false);
        return;
      }

      if (data.attendance === 'neither') {
        toast.success(
          "We'll miss you! Thank you for letting us know ❤️",
          { duration: 5000 }
        );
        setIsSubmitting(false);
        return;
      }

      setSubmittedName(data.fullName);
      setSubmittedAttendance(data.attendance);
      setIsSubmitted(true);
      setIsSubmitting(false);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Network error. Please check your connection and try again.');
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <SuccessScreen
        name={submittedName}
        attendance={submittedAttendance}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      <div className="w-full max-w-2xl mx-auto px-4 py-10 pb-16">
        <HeroCard />

        <FormProgress attendance={attendance} />

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <Section1PersonalInfo />
            <Section2RSVP />

            {showBhopal && (
              <>
                <Section3BhopalJourney />
                <Section4BhopalStay />
                <Section5FreeDays />
              </>
            )}

            {showWarud && (
              <>
                <Section6BhopalWarud />
                <Section7WarudStay />
                <Section8AfterWedding />
              </>
            )}

            {showSummary && <Section9TravelSummary />}

            <Section10Misc />

            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 px-6 rounded-xl font-sans font-semibold text-lg transition-all duration-150 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{
                  backgroundColor: isSubmitting
                    ? 'var(--muted-foreground)'
                    : 'var(--foreground)',
                  color: 'var(--primary-foreground)',
                }}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Submitting your RSVP…
                  </>
                ) : (
                  <>
                    Submit My RSVP
                    <span className="heart-pulse inline-block">❤️</span>
                  </>
                )}
              </button>

              <p
                className="text-center text-sm mt-3"
                style={{ color: 'var(--muted-foreground)' }}
              >
                Fields marked <span style={{ color: '#a34f42' }}>*</span> are
                required
              </p>
            </div>
          </form>
        </FormProvider>

        <footer
          className="text-center mt-10 text-sm"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <p className="font-semibold" style={{ color: 'var(--foreground)' }}>
            Vikas &amp; Anamika
          </p>
          <p className="mt-1">#Vimi · 2026–27</p>
        </footer>
      </div>
    </div>
  );
}